import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box,
  CircularProgress,
  useTheme
} from '@mui/material';
import { 
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { dashboardAPI, vendorAPI } from '../api/api';
import { getColorForName } from '../utils/colorUtils';

const VendorDistributionChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch both chart data and vendor data
        const [chartResponse, vendorResponse] = await Promise.all([
          dashboardAPI.getVendorDistribution(),
          vendorAPI.getVendors()
        ]);
        
        // Map the chart data to use standard names if available
        const mappedData = (chartResponse.data || []).map(item => {
          const vendor = (vendorResponse.data || []).find(v => 
            v.name.toLowerCase() === (item.name || '').toLowerCase()
          );
          
          // Use standard name if available, otherwise use the original name
          const displayName = vendor && vendor.standard_name && vendor.standard_name.trim() !== '' 
            ? vendor.standard_name 
            : item.name || 'Unknown';
          
          return {
            ...item,
            name: displayName
          };
        });
        
        setChartData(mappedData);
      } catch (error) {
        console.error('Error fetching vendor distribution data:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate total value for percentage calculation in vendor chart
  const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);

  if (loading) {
    return (
      <Card 
        elevation={0} 
        sx={{ 
          p: 3, 
          backgroundColor: 'background.paper',
          borderRadius: 3,
          height: '100%',
          boxShadow: 1
        }}
      >
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
            Vendor Distribution
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" height={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      elevation={0} 
      sx={{ 
        p: 3, 
        backgroundColor: 'background.paper',
        borderRadius: 3,
        height: '100%',
        boxShadow: 1
      }}
    >
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          Vendor Distribution
        </Typography>
        <Box height={300} display="flex" alignItems="center" justifyContent="center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false} // Remove arrows pointing to the chart
                outerRadius={80}
                innerRadius={50}
                dataKey="value"
                nameKey="name"
                label={false} // Remove labels from slices
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={getColorForName(entry.name, chartData, index)} 
                    stroke={theme.palette.background.paper}
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value) => [`${value.toFixed(2)}`, 'Amount']}
                labelFormatter={(label) => `Vendor: ${label}`}
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Legend 
                layout="horizontal" 
                verticalAlign="bottom" 
                align="center"
                wrapperStyle={{
                  paddingTop: '20px',
                  fontSize: '12px'
                }}
                formatter={(value, entry, index) => {
                  // Calculate percentage for each vendor
                  const percent = totalValue > 0 ? (entry.payload.value / totalValue) * 100 : 0;
                  return `${value} (${percent.toFixed(1)}%)`;
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default VendorDistributionChart;